import { BOOK_A_CALL_HREF } from '@pages/public/constants';
import abercrombieFitch from './assets/logos/abercrombie-fitch.png';
import athleta from './assets/logos/athleta.png';
import cotopaxi from './assets/logos/cotopaxi.png';
import dolceVita from './assets/logos/dolce-vita.png';
import landsEnd from './assets/logos/lands-end.png';
import madewell from './assets/logos/madewell.png';
import pact from './assets/logos/pact.png';
import reformation from './assets/logos/reformation.png';
import steveMadden from './assets/logos/steve-madden.png';
import vitaminA from './assets/logos/vitamin-a.png';
import type {
  EngageCard,
  FAQItem,
  PartnerLogo,
  ProofStat,
  SolutionCard
} from './types';

// Scrolls back to the Hero section's contact form rather than a dedicated page.
export const REQUEST_ACCESS_HREF = '#top';

export const HERO_LEDE =
  'ThredUp powers turnkey trade-in, branded resale, and bulk consignment programs for 50+ leading brands, driving incremental revenue, new customers, and measurable sustainability impact.';

// width/height are the files' true intrinsic pixel sizes so the browser can
// reserve the box before decode. CSS caps the rendered size.
export const PARTNER_LOGOS: PartnerLogo[] = [
  { src: reformation, alt: 'Reformation', width: 366, height: 47 },
  { src: madewell, alt: 'Madewell', width: 435, height: 102 },
  { src: cotopaxi, alt: 'Cotopaxi', width: 471, height: 153 },
  {
    src: abercrombieFitch,
    alt: 'Abercrombie & Fitch',
    width: 407,
    height: 135
  },
  { src: athleta, alt: 'Athleta', width: 396, height: 51 },
  { src: dolceVita, alt: 'Dolce Vita', width: 456, height: 90 },
  { src: steveMadden, alt: 'Steve Madden', width: 492, height: 75 },
  { src: landsEnd, alt: "Lands' End", width: 621, height: 81 },
  { src: pact, alt: 'Pact', width: 477, height: 182, small: true },
  { src: vitaminA, alt: 'Vitamin A', width: 453, height: 84 }
];

export const SOLUTION_CARDS: SolutionCard[] = [
  {
    id: 'clean-out',
    variant: 'flagship',
    tag: 'Most popular',
    title: 'Clean Out Program',
    description:
      'Let customers trade in pre-loved clothing from any brand in exchange for credit to shop with you — driving loyalty, repeat purchase, and new customer acquisition.',
    bullets: [
      'Multi-brand trade-in',
      'Custom landing page, branded kits or digital labels',
      'Launch in ~2 weeks, no tech work required'
    ]
  },
  {
    id: 'cash-out',
    variant: 'cashout',
    tag: 'Acquisition',
    title: 'Cash Out',
    description:
      'ThredUp sellers convert their resale earnings into store credit with your brand — bringing high-intent shoppers to your site, gift card in hand.',
    bullets: [
      'Featured on the Cash Out page on ThredUp.com',
      'Optional booster to drive conversion',
      'Weekly CSV of cash out customers'
    ]
  },
  {
    id: 'resale-shop',
    variant: 'shop',
    tag: 'On-site',
    title: 'Branded Resale Shop',
    description:
      'Launch a fully managed, white-label resale storefront — no tech build, no upfront cost, no monthly fees. We list, price, fulfill, and handle returns.',
    bullets: [
      'Leverage existing ThredUp inventory to stock your Resale Shop',
      'End-to-end fulfillment by ThredUp',
      'Stacks with Clean Out'
    ]
  },
  {
    id: 'bulk',
    variant: 'bulk',
    tag: 'Inventory',
    title: 'Dropshipping & Bulk Product',
    description:
      "Move returns, samples, gently used, and B-grade product through ThredUp's marketplace at scale — earning resale proceeds instead of liquidating.",
    bullets: [
      'Bulk consignment or drop ship via API',
      'You control pricing on drop-ship listings',
      'Monthly payouts on items sold'
    ]
  }
];

export const ENGAGE_CARDS: EngageCard[] = [
  {
    variant: 'guided',
    badge: 'Guided',
    number: '01 · Book a Strategy Call',
    title: 'Talk Through Your Resale Strategy',
    description:
      'Meet with ThredUp RaaS to walk through our ROI calculator and get recommendations tailored to your brand.',
    bullets: [
      "Explore your brand's resale opportunity and sustainability impact",
      'See how leading brands are driving value through resale',
      'Identify the best launch approach for your goals and customers',
      'Live Q&A with our partnerships team'
    ],
    ctaLabel: 'Book a 30-min call',
    ctaHref: BOOK_A_CALL_HREF
  },
  {
    variant: 'self-serve',
    badge: 'Self-serve',
    number: '02 · Request Portal Access',
    title: 'Calculate The Impact Of Resale',
    description:
      'Explore the potential revenue and impact of a trade-in or resale program on your own.',
    bullets: [
      "Estimate your brand's resale and sustainability potential",
      'See projected ROI based on your business inputs',
      'Get instant results, no meetings required',
      'Explore tailored opportunities at your own pace'
    ],
    ctaLabel: 'Request access',
    ctaHref: REQUEST_ACCESS_HREF
  }
];

export const PROOF_STATS: ProofStat[] = [
  {
    value: '47',
    suffix: '%',
    copy: 'of consumers are more likely to make a first-time purchase if trade-in credit is offered.'
  },
  {
    value: '60',
    suffix: '%',
    copy: 'of Gen Z and Millennial shoppers report that integrated resale features directly increase their trust in a brand.',
    featured: true
  },
  {
    value: '32',
    suffix: '%',
    copy: 'of retailers view resale as a necessary way to remain competitive.'
  },
  {
    value: '58',
    suffix: '%',
    copy: 'of retailers agree that lacking a resale presence creates a permanent structural disadvantage.'
  },
  {
    value: '42',
    suffix: '%',
    copy: 'of retailers say the biggest risk of not scaling resale is losing Gen Z and Millennial market share.'
  }
];

export const PROOF_SOURCE =
  'Source: ThredUp 2026 Resale Report (n = 3,000 US consumers)';

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'How long does it take to launch?',
    answer:
      'Clean Out and Cash Out programs typically go live ~2 weeks. Branded Resale Shop and Bulk Consignment are 4–8 weeks depending on catalog size and any API integration.',
    defaultOpen: true
  },
  {
    question: 'Are there any setup fees or monthly costs?',
    answer:
      'No setup costs and no monthly costs, ever. Your only investment is a 30% brand gift card contribution (20% when paired with a Branded Resale Shop), which directly funds the gift cards your customers receive. ThredUp will automatically deduct your gift card contribution before sending your monthly payment, so there are no invoices to pay, no bills to manage, and no additional payments required.'
  },
  {
    question:
      'How is this different from running a trade-in program ourselves?',
    answer:
      'We accept any brand, not just yours! Plus you skip the receive/grade/list/fulfill infrastructure, the customer service load, the marketplace, and the inventory risk.'
  },
  {
    question: 'Can I run more than one program?',
    answer:
      'Most of our top partners do. Clean Out + Resale Shop is the most common bundle (it unlocks the 80% gift-card co-fund). Cash Out is a Clean Out add-on. Bulk Consignment runs in parallel for inventory monetization.'
  }
];
